import type { Order } from "../types";
import { bonificoSiConfermaSuShopify, isBonificoDaVerificare } from "./BonificoButton";
import { ritiroDelRicambio } from "../sostituzione";
import { supplierReplacements } from "./Sostituzioni";
import { pieno, type StatoTone } from "./statoToni";

/** Colonna STATI della lista ordini, «una per riga» (Davide 2026-09-23: la
 *  variante A delle cinque provate sul canvas).
 *
 *  Era una griglia a due colonne di badge appesi in alto, larghi quanto il loro
 *  testo: la stessa etichetta cambiava posto da una riga all'altra e la
 *  colonna restava mezza vuota. Ora un fatto per riga, tutte le barre larghe
 *  uguali, centrate nell'altezza della card — e la colonna e' larga quanto la
 *  barra piu' lunga che puo' comparire (vedi `.griglia-ordini` in globals.css).
 *
 *  Meno etichette per gli stessi fatti: dell'avanzamento si dice solo il passo
 *  PIU' AVANTI, perche' contiene quelli prima — «LDV inviata» vuol gia' dire
 *  ordinato e merce pronta, «Spedito» vuol dire LDV partita (se non e' partita
 *  lo urla l'allarme «LDV mai inviata»). Prima comparivano insieme, e tre di
 *  loro erano verdi quasi uguali. */

/** Un fatto dell'ordine, detto una volta. */
export interface StatoRiga {
  key: string;
  label: string;
  tone: StatoTone;
  /** Il numero che accompagna il fatto (giorni, data, ×N · €): va a destra. */
  dettaglio?: string;
  title?: string;
  testId?: string;
}

/** Pagato / da pagare il fornitore ha senso solo per chi paghiamo a ordine
 *  (Davide 2026-06-08): per gli altri non e' un concetto. */
const PAGAMENTO_FORNITORE = new Set(["itamoby", "ityhome"]);

/** L'ordine delle righe E' la gerarchia: allarmi, dov'e' arrivato l'ordine,
 *  i soldi, il contorno. Fisso, cosi' da una card all'altra la fase sta
 *  sempre in cima (sotto un eventuale allarme). */
export function statiDellOrdine(order: Order): StatoRiga[] {
  const righe: StatoRiga[] = [];
  if (order.pickupMailMissing)
    righe.push({
      key: "ldv-mancante",
      label: "LDV mai inviata",
      tone: "ldv-mancante",
      testId: "pickup-mail-missing-badge",
      title:
        "Ordine spedito su Poleepo ma la mail di spedizione al fornitore non risulta mai inviata: il fornitore non ha la LDV. Invia la mail di ritiro o verifica IMAP dal dettaglio ordine.",
    });
  if (order.isDuplicate)
    righe.push({
      key: "doppio",
      label: "Doppio",
      tone: "doppio",
      title:
        "Doppione: Poleepo ha piu' ordini con questa reference. La lista ne mostra uno solo (il piu' avanzato). Controlla/pulisci su Poleepo.",
    });
  const passo = fase(order);
  if (passo) righe.push(passo);
  return [...righe, ...soldi(order), ...contorno(order)];
}

/** Dov'e' arrivato l'ordine: il passo piu' avanti, uno solo.
 *
 *  «Da ordinare» sta sopra lo stato del canale perche' e' un gesto che tocca a
 *  noi e non deve nascondersi; «Pronto» (READY_TO_SHIP del canale) sta sopra
 *  «Ordinato» perche' viene dopo. Sta invece SOTTO merce pronta, LDV e
 *  spedito, anche quando OM non ha registrato l'ordine (niente N°, costo o
 *  fattura: il backend dice ``to_send``): se il fornitore scrive «merce
 *  pronta» l'ordine ce l'ha, e un «Da ordinare» accanto inviterebbe a
 *  comprarlo due volte. Il buco resta visibile: manca il N° fornitore.
 *  «In corso» resta solo dove un flusso col fornitore non c'e' (magazzino,
 *  marketplace diretto); «Nuovo» e' il default di ogni ordine appena arrivato,
 *  e dirlo e' rumore. */
function fase(order: Order): StatoRiga | null {
  const invio = order.supplierSendState ?? null;
  if (order.state === "annullato") return { key: "fase", label: "Annullato", tone: "annullato" };
  // Il ricambio viaggia dopo l'originale: «Spedito» parla del pacco vecchio.
  if (ritiroDelRicambio(order)) {
    if (order.pickupMailSent)
      return {
        key: "fase",
        label: "Ricambio · LDV inviata",
        tone: "ldv-inviata",
        title: "Etichetta della sostituzione gia' mandata al fornitore per il ritiro del ricambio",
      };
    if (order.pickupReady)
      return {
        key: "fase",
        label: "Ricambio pronto",
        tone: "pronto-ritiro",
        title:
          "Merce pronta del ricambio: l'originale e' gia' partito, il ritiro del pezzo nuovo parte con l'etichetta della sostituzione",
      };
  }
  if (order.state === "spedito") return { key: "fase", label: "Spedito", tone: "spedito" };
  if (order.pickupMailSent) {
    const inviata = order.pickupMailSentAt ? new Date(order.pickupMailSentAt) : null;
    const valida = inviata && !Number.isNaN(inviata.getTime()) ? inviata : null;
    return {
      key: "fase",
      label: "LDV inviata",
      tone: "ldv-inviata",
      dettaglio: valida?.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }),
      title: valida
        ? `Mail LDV gia' inviata al fornitore (${valida.toLocaleString("it-IT")})`
        : "Mail LDV gia' inviata al fornitore",
    };
  }
  if (order.pickupReady)
    return {
      key: "fase",
      label: "Pronto ritiro",
      tone: "pronto-ritiro",
      title: "Mail 'merce pronta' ricevuta dal fornitore",
    };
  if (invio === "to_send")
    return {
      key: "fase",
      label: "Da ordinare",
      tone: "da-ordinare",
      title: "Ordine ancora da inviare al fornitore",
    };
  if (order.state === "pronto") return { key: "fase", label: "Pronto", tone: "pronto" };
  if (invio === "sent")
    return {
      key: "fase",
      label: "Ordinato",
      tone: "ordinato",
      title: "Ordine gia' inviato al fornitore",
    };
  if (order.state === "in_lavorazione") return { key: "fase", label: "In corso", tone: "in-corso" };
  return null;
}

/** Prima il cliente (incasso), poi il fornitore (pagamento). «Attesa
 *  bonifico» e' un «Da incassare» con la sua causa, quindi lo sostituisce.
 *  Con la LDV inviata l'ordine per noi e' concluso, e «Pagato» e' sottinteso
 *  come ordinato e merce pronta (Davide 2026-09-23); un «Da pagare» invece
 *  resta: e' un debito, non un passo fatto. */
function soldi(order: Order): StatoRiga[] {
  const righe: StatoRiga[] = [];
  if (isBonificoDaVerificare(order))
    righe.push({
      key: "incasso",
      label: "Attesa bonifico",
      tone: "bonifico",
      title: bonificoSiConfermaSuShopify(order)
        ? "Bonifico: quando arriva in banca premi 'Bonifico arrivato', OM lo segna pagato anche su Shopify"
        : "Bonifico bancario: verifica l'accredito in banca e conferma con il bottone 'Bonifico arrivato'",
    });
  // Anche sugli spediti: il paymentState collassa su "shipped", il credito no.
  else if (order.paymentState === "awaiting_payment" || order.customerUnpaid === true)
    righe.push({
      key: "incasso",
      label: "Da incassare",
      tone: "da-incassare",
      title: "Il cliente non ha ancora pagato quest'ordine",
    });
  const debito = debitoFornitore(order);
  if (debito) righe.push(debito);
  else if (
    order.paymentState === "paid" &&
    PAGAMENTO_FORNITORE.has(order.fornitore.kind) &&
    !order.pickupMailSent
  )
    righe.push({ key: "pagato", label: "Pagato", tone: "pagato" });
  return righe;
}

/** Il debito verso il fornitore, dalle sue due fonti (Davide 2026-09-01: «Da
 *  pagare e Ordinato non pagato sono la stessa cosa»): il flag
 *  ``supplierUnpaid``, che vive anche sugli spediti, e il paymentState, che in
 *  piu' sa da quanto. Arancio appena ordinato, rosso dai 2 giorni in su. */
function debitoFornitore(order: Order): StatoRiga | null {
  const ps = order.paymentState;
  const daPaymentState =
    PAGAMENTO_FORNITORE.has(order.fornitore.kind) &&
    (ps === "ordered_unpaid" || ps === "unpaid_2days");
  if (order.supplierUnpaid !== true && !daPaymentState) return null;
  const giorni = ps === "unpaid_2days" ? giorniDa(order.createdAtPoleepo) : null;
  return {
    key: "debito",
    label: "Da pagare",
    // Senza data dell'ordine: «almeno due», mai un numero inventato.
    dettaglio: giorni != null ? `${giorni} gg` : ps === "unpaid_2days" ? "2+ gg" : undefined,
    tone: ps === "ordered_unpaid" ? "da-pagare-atteso" : "da-pagare",
    title: "Ordine non ancora pagato al fornitore",
  };
}

/** Giorni FERIALI da ``createdAtPoleepo`` (Davide 28/09: il weekend non
 *  conta): i lun-ven dopo il giorno dell'ordine fino a oggi compreso. Stessa
 *  regola del backend (``orders/giorni_feriali.py``), che decide il rosso. */
function giorniDa(iso: string | null | undefined): number | null {
  const t = iso ? Date.parse(iso) : Number.NaN;
  if (!Number.isFinite(t)) return null;
  const giorno = new Date(t);
  giorno.setHours(0, 0, 0, 0);
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  if (giorno > oggi) return null;
  let feriali = 0;
  while (giorno < oggi) {
    giorno.setDate(giorno.getDate() + 1);
    if (giorno.getDay() !== 0 && giorno.getDay() !== 6) feriali += 1;
  }
  return feriali;
}

function contorno(order: Order): StatoRiga[] {
  const righe: StatoRiga[] = [];
  if (order.merceProntaToday && order.state === "spedito")
    righe.push({
      key: "gia-spedita",
      label: "Già spedita",
      tone: "gia-spedita",
      title:
        "Email 'Merce pronta al ritiro' ricevuta oggi, ma l'ordine risulta gia' spedito su Poleepo (nessun ritiro da preparare)",
    });
  if (order.inWarehouse)
    righe.push({
      key: "magazzino",
      label: "In magazzino",
      tone: "in-casa",
      title:
        "Pezzo nel magazzino BS COM (app MATECPRO Logistics, azienda BS COM): lo spedisci tu, nessun invio al fornitore",
    });
  const rispedizioniAs =
    Number(order.raw?.as_replacement_count ?? 0) ||
    (typeof order.raw?.as_replacement_sent_at === "string" ? 1 : 0);
  if (rispedizioniAs > 0)
    righe.push({
      key: "sost-as",
      label: "Sost. AS",
      tone: "sost-as",
      dettaglio: rispedizioniAs > 1 ? `×${rispedizioniAs}` : undefined,
      title: "Rispedizione sostituzione gia' inviata ad AllService (stesso file ordine)",
    });
  const sost = supplierReplacements(order);
  if (sost.count > 0)
    righe.push({
      key: "sost-fornitore",
      label: "Sost.",
      tone: "sost-fornitore",
      // Euro senza centesimi (Davide 2026-09-01): qui non decidono nulla.
      dettaglio: `×${sost.count}${sost.costTotal > 0 ? ` · €${Math.round(sost.costTotal).toLocaleString("it-IT")}` : ""}`,
      title:
        "Sostituzioni fornitore (mail 'sost' Itamoby/Ityhome) registrate su questo ordine, con il costo totale addebitato",
    });
  return righe;
}

/** La forma dei badge di stato (`FORMA_BADGE`) stesa sulla colonna:
 *  etichetta a sinistra, dettaglio a destra. Su telefono la card va in
 *  colonna e le barre tornano larghe quanto il testo, affiancate. */
const BARRA =
  "flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md border-[1.5px] px-2 py-[3px] text-[11px] font-semibold leading-tight max-md:w-auto";

export function StatiCell({ order }: { order: Order }) {
  return (
    <div
      data-testid={`order-stati-${order.id}`}
      className="min-w-0 flex flex-col justify-center gap-1 max-md:flex-row max-md:flex-wrap"
    >
      {statiDellOrdine(order).map((s) => (
        <span
          key={s.key}
          title={s.title}
          data-testid={s.testId}
          style={pieno(s.tone)}
          className={BARRA}
        >
          {s.label}
          {s.dettaglio && <span>{s.dettaglio}</span>}
        </span>
      ))}
    </div>
  );
}
